import { reactive } from "vue";
import { useI18n } from "vue-i18n";
import { ResponseResult } from "../models/response-result";

export interface Toast {
  id: number;
  type: "info" | "error" | "success" | "warning";
  title: string;
  message: string;
  duration: number;
  dismissible: boolean;
}

export interface ToastProps {
  title?: string;
  message?: string;
  options?: Partial<Pick<Toast, "duration" | "dismissible">>;
  statusCode?: number;
}

const toastState = reactive<{ toasts: Toast[] }>({
  toasts: [],
});

let toastIdCounter = 0;

export function useToast() {
  const { t } = useI18n();
  const showToast = ({
    type = "info",
    title = "",
    message = "",
    duration = 5000,
    dismissible = true,
  }: Partial<Omit<Toast, "id">>) => {
    const id = ++toastIdCounter;
    const toast: Toast = {
      id,
      type,
      title,
      message,
      duration,
      dismissible,
    };
    toastState.toasts.push(toast);

    setTimeout(() => {
      const index = toastState.toasts.findIndex((t) => t.id === id);
      if (index !== -1) toastState.toasts.splice(index, 1);
    }, duration);

    return id;
  };
  //   (
  //     message: string,
  //     title = "Erro",
  //     options: Partial<Pick<Toast, "duration" | "dismissible">> = {},
  //     statusCode?: number,
  //   )

  const showError = ({
    title = "Erro",
    message,
    options = {},
    statusCode,
  }: ToastProps): number => {
    if (statusCode) {
      title = import.meta.env.DEV
        ? title
        : t(ResponseResult.STATUS_CODE_MESSAGES[statusCode].titleKey);
      message = import.meta.env.DEV
        ? message
        : t(ResponseResult.STATUS_CODE_MESSAGES[statusCode].messageKey);
    }
    return showToast({ type: "error", title, message, ...options });
  };

  const showSuccess = ({
    title = "Sucesso",
    message,
    options = {},
  }: ToastProps): number =>
    showToast({ type: "success", title, message, ...options });

  const showWarning = ({
    title = "Aviso",
    message,
    options = {},
  }: ToastProps): number =>
    showToast({ type: "warning", title, message, ...options });

  const showInfo = ({
    title = "Informação",
    message,
    options = {},
  }: ToastProps): number =>
    showToast({ type: "info", title, message, ...options });

  const removeToast = (id: number) => {
    const index = toastState.toasts.findIndex((t) => t.id === id);
    if (index !== -1) toastState.toasts.splice(index, 1);
  };

  const clearAllToasts = () => {
    toastState.toasts.length = 0;
  };

  return {
    showToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts,
    toastState, // expose this so the component can use it
  };
}
